/**
 * SFX timeline chips for the Composer UI.
 *
 * After the Remotion → Hyperframes migration the per-scene SFX recipe system
 * (which lived in 4 specialised scene files) has been replaced by the
 * Hyperframes audio model (`<audio class="clip">`). The Composer timeline
 * still wants to show a single SFX chip per scene clip so users can mute /
 * suppress the bundled SFX — we emit a "fallback" chip per scene, which the
 * timeline renders as a translucent span.
 */
import type { FormatId } from "../hyperframes/format.js";
import type { ReelScene } from "../scenes/types.js";

/** SFX sound name — kept loose so the Timeline UI doesn't have to import a
 *  specific enum after the SfxBank deletion. */
export type SfxName = string;

/**
 * `kind` is a discriminated union so the Timeline UI can keep its existing
 * branch logic. After the Remotion → Hyperframes migration we only emit
 * `fallback` chips (one per scene), but the `beat` shape is preserved as a
 * type so future scene-baked SFX recipes can be added without breaking the UI.
 */
export type EmbeddedSfxChipDesc =
  | {
      kind: "beat";
      uid: string;
      sceneUid: string;
      sceneId: string;
      embedIndex: number;
      sound: SfxName;
      fromSec: number;
    }
  | {
      kind: "fallback";
      uid: string;
      sceneUid: string;
      sceneId: string;
      fromSec: number;
      durSec: number;
    };

export function listEmbeddedSfxChips(
  layout: Array<{ scene: ReelScene; fromFrames: number; durFrames: number }>,
  fps: number,
  _format: FormatId,
): EmbeddedSfxChipDesc[] {
  return layout.map((seg) => ({
    kind: "fallback" as const,
    uid: `emb:${seg.scene.uid}:fallback`,
    sceneUid: seg.scene.uid,
    sceneId: seg.scene.sceneId,
    fromSec: seg.fromFrames / fps,
    durSec: seg.durFrames / fps,
  }));
}
